import { isValid, sub } from "date-fns";
import {
  DATE_TIME_FORMAT,
  INTEG,
  MASTER,
  PROD,
  GAMMA,
  SHIFT_PLANNING,
  REGION,
  DEFAULT_REFRESH_INTERVAL,
  ROUTE_VIEW,
  SECONDS_PER_MINUTE,
  DECIMAL_BASE,
  TIMEOUT_STRING,
} from "./Constants";
import { getStationCode } from "../network/NetworkUtil";
import {
  getFromLocalStorage,
  saveIntoLocalStorage,
} from "../overview/utils/utils";

/**
 * Returns the current time in format [ MonthName dd, YYYY, HH:MM:SS GMT+__:__ ]
 * This pattern may slightly change according to current locale.
 * @param currentDate
 * @param locale
 * @return string
 */
export const getFormattedTime = (currentDate, locale) => {
  return new Intl.DateTimeFormat(locale, DATE_TIME_FORMAT).format(currentDate);
};

export function getDomain() {
  const url = window.location.href;
  return url.includes(INTEG) ? INTEG : url.includes(MASTER) ? MASTER : PROD;
}

export function getShiftPlanningURL() {
  const stationCode = getStationCode();
  const domain = getDomain() !== PROD ? GAMMA : PROD;
  const region = getRegion();
  return `https://${domain}-${region}${SHIFT_PLANNING.IFRAME_URL_PATH}${stationCode}${SHIFT_PLANNING.IFRAME_CYCLES_URL_PATH}`;
}

/**
 * Appends the "_IN" suffix to the given process name if the current region is "IN" (India).
 *
 * @param {string} processName - The original process name.
 * @returns {string} - The modified process name with the "_IN" suffix, if applicable.
 */
export function appendRegionSuffix(processName) {
  return getRegion() === REGION.IN ? processName + "_IN" : processName;
}

export function getRegion() {
  const url = window.location.href;
  return url.includes("eu.")
    ? REGION.EU
    : url.includes("jp.")
      ? REGION.JP
      : url.includes("sccin-")
        ? REGION.IN
        : url.includes("sccwebsitein")
          ? REGION.IN
          : REGION.NA;
}

export function castToString(str) {
  if (str.toString) {
    return str.toString();
  } else {
    return "";
  }
}

export function convertTimeToDate() {}

export function validateTimes(
  processTime,
  currentDate,
  opsClockData,
  isOpsClockDataBeingUpdated,
  processName,
  resetTime,
) {
  /**
   * Handler function that both checks and alters process start and end times
   * @param  {Date}    processTime Start/End time of process / break
   * @param  {Date}      currentDate      Current date selected in "YYYY-MM-DD" format
   * @param  {Object}  opsClockData data from the opsclock
   * @param  {Boolean} isOpsClockDataBeingUpdated flag to check if the data is being updated or is the default
   * @param  {String} processName name of the process
   * @param  {Date} resetTime reset time for station
   * @return {Date}                      Altered start and end times
   */

  // Type checking
  if (!(processTime instanceof Date)) {
    throw TypeError;
  }

  // Extracting current date
  let currYear, currMonth, currDate;
  [currYear, currMonth, currDate] = currentDate.split("-");
  currentDate = new Date(currYear, parseInt(currMonth, 10) - 1, currDate);

  let updatedProcessTime;
  updatedProcessTime = getCorrectDateForProcessTime(
    processTime,
    currentDate,
    opsClockData,
    isOpsClockDataBeingUpdated,
    processName,
    resetTime,
  );

  return updatedProcessTime;
}

export function isValidNumber(number) {
  return !isNaN(parseFloat(number));
}

export const checkIfIntervalsAreValid = (
  newProcessStartTime,
  newProcessEndTime,
) => {
  return (
    newProcessStartTime &&
    newProcessEndTime &&
    newProcessStartTime < newProcessEndTime
  );
};

const getCorrectDateForProcessTime = (
  processTime,
  currentDate,
  opsClockData,
  isOpsClockDataBeingUpdated,
  processName,
  resetTime,
) => {
  const maxEpochValue = new Date(8640000000000000).getTime();
  const dayOffset = 24 * 60 * 60 * 1000;
  const processTimeWithDispatchDateAsCurrentDate = new Date(processTime);
  processTimeWithDispatchDateAsCurrentDate.setDate(currentDate.getDate());
  processTimeWithDispatchDateAsCurrentDate.setMonth(currentDate.getMonth());
  processTimeWithDispatchDateAsCurrentDate.setFullYear(
    currentDate.getFullYear(),
  );
  const processTimeWithDispatchDateAsPreviousDay = new Date(
    processTimeWithDispatchDateAsCurrentDate.getTime() - dayOffset,
  );

  let minInductOrSortStartTime = isValid(resetTime)
    ? sub(resetTime, { days: 1 })
    : maxEpochValue;
  let maxDispatchEndTime = isValid(resetTime) ? resetTime : 0;
  let clockEvents = opsClockData.opsClock
    ? opsClockData.opsClock.clockEvents
    : [];

  clockEvents.forEach((clockEvent) => {
    if (clockEvent.process === "INDUCT" || clockEvent.process === "TWILIGHT") {
      minInductOrSortStartTime = Math.min(
        minInductOrSortStartTime,
        clockEvent.timeIntervals[0].startTime * 1000,
      );
    }

    if (clockEvent.process === "DISPATCH") {
      maxDispatchEndTime = Math.max(
        maxDispatchEndTime,
        clockEvent.timeIntervals[0].endTime * 1000,
      );
    }
  });

  const previousDateTime = processTimeWithDispatchDateAsPreviousDay.getTime();

  if (
    (previousDateTime >= minInductOrSortStartTime &&
      previousDateTime <= maxDispatchEndTime) ||
    (isOpsClockDataBeingUpdated &&
      (processName === "INDUCT" || processName === "TWILIGHT") &&
      previousDateTime >= minInductOrSortStartTime)
  ) {
    return processTimeWithDispatchDateAsPreviousDay;
  }

  if (
    processTimeWithDispatchDateAsCurrentDate.getTime() >=
      minInductOrSortStartTime &&
    processTimeWithDispatchDateAsCurrentDate.getTime() <= maxDispatchEndTime
  ) {
    return processTimeWithDispatchDateAsCurrentDate;
  }

  if (isOpsClockDataBeingUpdated) return processTime;
};

export const isWithInTheInterval = (time, startTime, endTime) => {
  return time >= startTime && time <= endTime;
};

export const isOverlappingTimeIntervals = (timeInterval1, timeInterval2) => {
  const getMinutes = (s) => {
    const p = s.split(":").map(Number);
    return p[0] * 60 + p[1];
  };
  return (
    getMinutes(timeInterval1.localEndTime) >
      getMinutes(timeInterval2.localStartTime) &&
    getMinutes(timeInterval2.localEndTime) >
      getMinutes(timeInterval1.localStartTime)
  );
};

export const chainWalk = (func, defaultValue) => {
  try {
    const value = func();
    return value === null || value === undefined ? defaultValue : value;
  } catch (e) {
    return defaultValue;
  }
};

export function getAmxlRouteType(routeCode) {
  return routeCode.includes("_")
    ? routeCode.split("_")[0]
    : routeCode.substring(0, 2);
}

export const getAliasByEmail = (alias) => {
  let alias_val;
  if (alias.includes("@")) {
    alias_val = alias.split("@")[0].trim();
  } else {
    alias_val = alias.trim();
  }
  return alias_val;
};

export const isStringEmpty = (str) => {
  return !str || str.trim().length === 0;
};

/**
 * Converts milliseconds to time format HH:MM
 * @param {string} milliseconds - The milliseconds to convert
 * @returns {string} Time in HH:MM format, or "-" if milliseconds is falsy
 */
export function convertMillisecondsToHHMM(milliseconds) {
  if (!milliseconds) {
    return "-";
  }
  const time = new Date(parseInt(milliseconds, 10));
  return (
    ("0" + time.getHours()).slice(-2) +
    ":" +
    ("0" + time.getMinutes()).slice(-2)
  );
}

/**
 * Returns "NA" if value is falsy or equals "...", otherwise returns the value itself
 * @param {*} value - The value to check
 * @returns {string|*} "NA" if value is falsy or "...", otherwise returns value
 */
export function naIfDefault(value) {
  return !value || value === "..." ? "NA" : value;
}

/**
 * This method return the refresh interval in milliseconds
 * @returns {number}
 */
export function getStationRefreshInterval(stationConfig) {
  if (stationConfig) {
    return stationConfig.refreshInterval;
  }
  return DEFAULT_REFRESH_INTERVAL;
}

/**
 * Saves filter state into local storage along with current station code.
 * It also translates the newState into JSON.
 * @param filterKey
 * @param newState
 */
export function saveFilterStateRouteView(filterKey, newState) {
  try {
    newState.viewType = ROUTE_VIEW;
    const record = JSON.stringify({
      data: newState,
      stationCode: getStationCode(),
    });
    saveIntoLocalStorage(filterKey, record);
  } catch (e) {}
}

/**
 * Reads filter state from local storage by translating it into Json.
 * If current station code is different the persisted one, it returns null.
 * @param filterKey
 */
export function readFilterStateWithFilterKey(filterKey) {
  try {
    const json = getFromLocalStorage(filterKey) || null;
    if (json) {
      const record = JSON.parse(json);
      if (record.stationCode === getStationCode()) {
        return record.data;
      }
    }
  } catch (ignored) {}
  return null;
}

/**
 * Converts a time string in HH:MM or HH:MM:SS format to total seconds
 * @param {string} time - The time string to convert in format "HH:MM" or "HH:MM:SS"
 * @returns {number} Total number of seconds. Returns 0 if time is falsy.
 * For HH:MM format: hours * 3600 + minutes * 60
 * For HH:MM:SS format: hours * 3600 + minutes * 60 + seconds
 */
export function getTimeInSecond(time) {
  if (!time) {
    return 0;
  }
  const splitTime = time.split(":");
  if (splitTime.length > 2) {
    return (
      parseInt(splitTime[0], DECIMAL_BASE) *
        SECONDS_PER_MINUTE *
        SECONDS_PER_MINUTE +
      parseInt(splitTime[1], DECIMAL_BASE) * SECONDS_PER_MINUTE +
      parseInt(splitTime[2], DECIMAL_BASE)
    );
  } else {
    return (
      parseInt(splitTime[0], DECIMAL_BASE) * SECONDS_PER_MINUTE +
      parseInt(splitTime[1], DECIMAL_BASE)
    );
  }
}

/**
 * Converts epoch milliseconds to local time in 24-hour format (HH:mm:ss)
 * @param {number} epochTime - The epoch time in milliseconds
 * @returns {string} The formatted time string in local time (e.g. "14:30:45")
 */
export function convertEpochMillisToLocalTime(epochTime) {
  const options = {
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: false,
  };
  const date = new Date(epochTime);
  return date.toLocaleString("en-GB", options);
}

/**
 * Input is epoch, output is date-time in browser local time zone
 * For eg:
 *  Input: 1637145000000
 *  Output: 2021/11/17 16:00:00 (Sweden date time format)
 * Ref: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleString
 */
export function convertEpochToLocaleFormat(epoch) {
  if (epoch && typeof epoch === "number") {
    return new Date(epoch).toLocaleString("sv");
  }
  return "";
}

/**
 * Gets country code from parent window
 * @returns {string} Country code
 */
export async function getCountryCode() {
  try {
    const { getCountryCodeFromParent } = await import(
      "../handler/ParentDataHandler"
    );
    let parentCountry = await getCountryCodeFromParent();

    if (parentCountry === TIMEOUT_STRING) {
      parentCountry = await getCountryCodeFromParent();
    }

    if (parentCountry && parentCountry !== TIMEOUT_STRING) {
      return parentCountry;
    }
  } catch (error) {
    console.log("Could not get country code from parent:", error);
  }
  return "";
}



// WEBPACK FOOTER //
// ./src/components/util/Util.js